import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  PropsWithChildren,
  createContext,
  isValidElement,
  useContext
} from 'react';
import { createPortal } from 'react-dom';
import { css } from '@emotion/css';
import { keyframes } from '@emotion/react';
import { Color } from '~/constants/css';
import { APP_SHELL_KEYBOARD_INSET_STYLE } from '~/constants/appShell';
import { isMobile, isTablet } from '~/helpers';
import Icon from '~/components/Icon';
import ErrorBoundary from '~/components/ErrorBoundary';
import ModalFooter, { ModalFooterContext } from './Footer';
import { lockBodyScroll } from '~/helpers/bodyScrollLock';

function safeRender(node: React.ReactNode): React.ReactNode {
  if (isValidElement(node)) return node;
  if ((node as any)?.$$typeof === Symbol.for('react.portal')) {
    return node;
  }
  if (
    typeof node === 'string' ||
    typeof node === 'number' ||
    typeof node === 'boolean'
  )
    return node;
  if (!node) return null;
  if (Array.isArray(node)) {
    return node.map((n, i) => (
      <React.Fragment key={i}>{safeRender(n)}</React.Fragment>
    ));
  }
  return null;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const slideOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
`;

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: ModalSize;
  showCloseButton?: boolean;
  hasHeader?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  style?: React.CSSProperties;
  priority?: boolean;
  /** A file opened from the floating companion window must appear above it. */
  aboveAssistant?: boolean;
  modalLevel?: number;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  loading?: boolean;
  preventBodyScroll?: boolean;
  animationDuration?: number;
  portalTarget?: HTMLElement;
  allowOverflow?: boolean;
  bodyPadding?: string | number;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /** Used for error boundary reporting - identifies which modal threw an error */
  modalKey?: string;
  /** An XP or coin activity: Zero and Ciel may show it but never act in it. */
  xpActivity?: boolean;
}

const sizeMap: Record<ModalSize, { width: string; maxWidth: string }> = {
  sm: { width: '90vw', maxWidth: 'calc(400 * var(--px))' },
  md: { width: '90vw', maxWidth: 'calc(600 * var(--px))' },
  lg: { width: '90vw', maxWidth: 'calc(800 * var(--px))' },
  xl: { width: '90vw', maxWidth: 'calc(1200 * var(--px))' },
  fullscreen: { width: '100vw', maxWidth: '100vw' }
};

const deviceIsMobile = isMobile(navigator);
const deviceIsTablet = isTablet(navigator);

function stopModalEventPropagation(event: React.SyntheticEvent) {
  event.stopPropagation();
}

function trapModalFocus(event: KeyboardEvent, modal: HTMLDivElement) {
  if (event.defaultPrevented) return;

  const focusableElements = Array.from(
    modal.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter(
    (element) =>
      element.getAttribute('aria-hidden') !== 'true' &&
      (element.offsetWidth > 0 || element.offsetHeight > 0)
  );

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  const activeElement = modal.ownerDocument.activeElement;

  if (!firstElement || !lastElement) {
    event.preventDefault();
    modal.focus();
    return;
  }

  if (event.shiftKey) {
    if (
      activeElement === modal ||
      activeElement === firstElement ||
      !modal.contains(activeElement)
    ) {
      event.preventDefault();
      lastElement.focus();
    }
    return;
  }

  if (
    activeElement === modal ||
    activeElement === lastElement ||
    !modal.contains(activeElement)
  ) {
    event.preventDefault();
    firstElement.focus();
  }
}

let modalCounter = 0;
const openModals = new Set<number>();

const getNextModalId = () => ++modalCounter;

const getZIndex = (level: number) => 9_999_999 + level * 1000;

const Modal = forwardRef<HTMLDivElement, PropsWithChildren<ModalProps>>(
  (
    {
      isOpen,
      onClose,
      title,
      size = 'md',
      showCloseButton = true,
      hasHeader = true,
      closeOnBackdropClick = true,
      closeOnEscape = true,
      className = '',
      style = {},
      priority = false,
      aboveAssistant = false,
      modalLevel,
      header,
      footer,
      loading = false,
      preventBodyScroll = true,
      animationDuration = 200,
      portalTarget,
      allowOverflow = false,
      bodyPadding,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      modalKey,
      xpActivity = false,
      children
    },
    ref
  ) => {
    // A window opened from inside an XP activity (a game's success or
    // challenge window) is part of it: it renders beside the activity in the
    // DOM, so the mark travels through React instead.
    const insideXpActivity = useContext(XpActivityContext);
    const marksXpActivity = xpActivity || insideXpActivity;
    const [isAnimating, setIsAnimating] = useState(false);
    const [shouldRender, setShouldRender] = useState(isOpen);
    const modalRef = useRef<HTMLDivElement>(null);
    const backdropRef = useRef<HTMLDivElement>(null);
    const previousActiveElement = useRef<HTMLElement | null>(null);
    const [modalId] = useState(getNextModalId);

    // Create a unique container for this modal instance to prevent portal conflicts
    const [container] = useState(() => {
      const el = document.createElement('div');
      el.setAttribute('data-modal-container', String(modalId));
      el.setAttribute('translate', 'no');
      el.className = 'notranslate';
      return el;
    });
    const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
    const [footerContainer, setFooterContainer] = useState<HTMLDivElement | null>(null);
    const footerHost = useMemo(
      () => ({ container: footerContainer, fullscreen: size === 'fullscreen' }),
      [footerContainer, size]
    );

    const resolvedBodyPadding =
      bodyPadding !== undefined
        ? typeof bodyPadding === 'number'
          ? `${bodyPadding}px`
          : bodyPadding
        : deviceIsMobile
          ? '0.75rem'
          : '1.25rem';
    // A flex container that scrolls drops its bottom padding from the
    // scrollable overflow region, so the last row of a scrolled body ends up
    // flush against the modal edge. Rendering that space as a transparent
    // bottom border keeps it outside the scrollport, where it always shows.
    // Only a single length can become a border width; a shorthand keeps the
    // plain padding it asked for.
    const bodyBottomSpacingIsBorder = !resolvedBodyPadding.trim().includes(' ');

    useLayoutEffect(() => {
      const target = portalTarget || document.getElementById('modal');
      if (target && container) {
        target.appendChild(container);
        setPortalRoot(target);
        return () => {
          setPortalRoot(null);
          if (container.parentNode === target) {
            target.removeChild(container);
          }
        };
      }
    }, [portalTarget, container]);

    const currentLevel = useMemo(() => {
      if (modalLevel !== undefined) return modalLevel;
      if (priority) return 1;
      return openModals.size;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalLevel, priority, modalId]);

    const zIndex = useMemo(
      () => aboveAssistant ? 2147483000 : getZIndex(currentLevel),
      [aboveAssistant, currentLevel]
    );

    useEffect(() => {
      if (isOpen) {
        openModals.add(modalId);
      } else {
        openModals.delete(modalId);
      }

      return () => {
        openModals.delete(modalId);
      };
    }, [isOpen, modalId]);

    // For width behavior only: treat tablet-landscape like desktop
    const isLandscape =
      typeof window !== 'undefined' && window.innerWidth > window.innerHeight;

    const { width, maxWidth, height } = useMemo(() => {
      const baseSize = sizeMap[size];

      if (size === 'fullscreen') {
        return {
          width: '100%',
          maxWidth: '100vw',
          height: '100%'
        };
      }

      if (deviceIsTablet) {
        if (isLandscape) {
          return {
            ...baseSize,
            height: 'auto'
          };
        }
        return {
          width: '85vw',
          maxWidth: baseSize.maxWidth,
          height: 'auto'
        };
      }

      if (deviceIsMobile) {
        return {
          width: '100vw',
          maxWidth: size === 'sm' ? '350px' : '100vw',
          height: 'auto'
        };
      }

      return {
        ...baseSize,
        height: 'auto'
      };
    }, [size, isLandscape]);

    useEffect(() => {
      if (!preventBodyScroll || !isOpen) return;

      const targetDocument = portalTarget?.ownerDocument ?? document;
      const bodyElement = targetDocument?.body;
      if (!bodyElement) return;

      return lockBodyScroll(bodyElement);
    }, [isOpen, preventBodyScroll, portalTarget]);

    useEffect(() => {
      if (!isOpen) return;

      const targetDocument = portalTarget?.ownerDocument ?? document;
      function handleDocumentKeyDown(event: KeyboardEvent) {
        if (event.defaultPrevented || event.isComposing || event.keyCode === 229) {
          return;
        }
        const topModalId = Math.max(...Array.from(openModals));
        if (modalId !== topModalId) return;
        if (event.key === 'Escape' && closeOnEscape) {
          event.preventDefault();
          event.stopPropagation();
          onClose();
          return;
        }
        // Zero and Ciel's floating window sits above modals and keeps its
        // own keyboard focus.
        const fromAgentWindow = (event.target as Element | null)?.closest?.(
          '[data-website-agent-ui]'
        );
        if (event.key === 'Tab' && modalRef.current && !fromAgentWindow) {
          trapModalFocus(event, modalRef.current);
        }
      }

      // Let a search field, editor or nested popup consume Escape first.
      targetDocument.addEventListener('keydown', handleDocumentKeyDown);
      return () =>
        targetDocument.removeEventListener(
          'keydown',
          handleDocumentKeyDown
        );
    }, [closeOnEscape, isOpen, onClose, modalId, portalTarget]);

    useEffect(() => {
      if (!isOpen) return;

      const targetDocument = portalTarget?.ownerDocument ?? document;
      const targetWindow = targetDocument.defaultView ?? window;
      const previouslyFocusedElement = targetDocument.activeElement as HTMLElement | null;
      previousActiveElement.current = previouslyFocusedElement;
      const focusTimer = targetWindow.setTimeout(() => {
        const modal = modalRef.current;
        if (modal && !modal.contains(targetDocument.activeElement)) {
          modal.focus();
        }
        if (allowOverflow && backdropRef.current) {
          backdropRef.current.scrollTop = 0;
        }
      }, 50);

      return () => {
        targetWindow.clearTimeout(focusTimer);
        if (previousActiveElement.current !== previouslyFocusedElement) return;
        if (previouslyFocusedElement?.isConnected) {
          previouslyFocusedElement.focus();
        }
        previousActiveElement.current = null;
      };
    }, [isOpen, allowOverflow, portalTarget]);

    useEffect(() => {
      if (isOpen) {
        setShouldRender(true);
        setIsAnimating(false);
      } else if (shouldRender) {
        setIsAnimating(true);

        const endTimer = setTimeout(() => {
          setShouldRender(false);
          setIsAnimating(false);
        }, animationDuration);

        return () => clearTimeout(endTimer);
      }
    }, [isOpen, shouldRender, animationDuration]);

    const handleBackdropClick = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        if (closeOnBackdropClick && event.target === backdropRef.current) {
          onClose();
        }
      },
      [closeOnBackdropClick, onClose]
    );

    if (!shouldRender) return null;

    const modalContent = (
      <ErrorBoundary componentPath={modalKey || 'Modal/index'}>
        <div
          ref={backdropRef}
          translate="no"
          className={`${css`
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            /* Fixed positioning is relative to the LAYOUT viewport, which the
               on-screen keyboard does not shrink; without this the backdrop —
               and the inputs it centers — extend behind the keyboard. */
            bottom: ${APP_SHELL_KEYBOARD_INSET_STYLE};
            z-index: ${zIndex};
            display: flex;
            width: 100%;
            align-items: ${allowOverflow ? 'flex-start' : 'center'};
            justify-content: center;
            background-color: rgba(0, 0, 0, 0.5);
            animation: ${!isAnimating ? fadeIn : fadeOut} ${animationDuration}ms
              ease-out;
            padding: ${size === 'fullscreen' || deviceIsMobile
              ? '0'
              : deviceIsTablet
                ? '1rem'
                : '2rem'};
            ${allowOverflow && size !== 'fullscreen'
              ? `padding-top: ${
                  deviceIsMobile
                    ? '0.75rem'
                    : deviceIsTablet
                      ? '1.25rem'
                      : '2rem'
                };`
              : ''}
            overflow-y: auto;
            touch-action: manipulation;
          `} notranslate`}
          onClick={handleBackdropClick}
          onMouseDown={stopModalEventPropagation}
          onMouseUp={stopModalEventPropagation}
          onPointerCancel={stopModalEventPropagation}
          onPointerDown={stopModalEventPropagation}
          onPointerMove={stopModalEventPropagation}
          onPointerUp={stopModalEventPropagation}
          onTouchEnd={stopModalEventPropagation}
          onTouchStart={stopModalEventPropagation}
        >
          <div
            ref={ref || modalRef}
            className={css`
              position: relative;
              width: ${width};
              max-width: ${maxWidth};
              ${height !== 'auto' ? `height: ${height};` : ''}
              ${size === 'fullscreen'
                ? 'max-height: 100%;'
                : allowOverflow
                  ? ''
                  : 'max-height: 95%;'}
              background-color: white;
              border-radius: ${size === 'fullscreen'
                ? '0'
                : deviceIsMobile
                  ? '8px'
                  : '12px'};
              box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
              animation: ${!isAnimating ? slideIn : slideOut}
                ${animationDuration}ms ease-out;
              display: flex;
              flex-direction: column;
              overflow: hidden;
              ${className}

              &:focus {
                outline: none;
              }
            `}
            style={style}
            onClick={(e) => e.stopPropagation()}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby}
            {...(marksXpActivity ? { 'data-agent-no-play': '' } : {})}
          >
            {hasHeader && (header || title || showCloseButton) && (
              <div
                className={css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  padding: ${deviceIsMobile ? '1rem' : '1.5rem'};
                  border-bottom: none;
                  background-color: white;
                  ${size === 'fullscreen'
                    ? ''
                    : 'border-radius: 12px 12px 0 0;'}
                  flex-shrink: 0;
                `}
              >
                <div
                  className={css`
                    flex: 1;
                    font-size: ${deviceIsMobile || deviceIsTablet
                      ? '1.6rem'
                      : '1.8rem'};
                    font-weight: 600;
                    color: ${Color.black()};
                    margin-right: 1rem;
                  `}
                  id={ariaLabelledby}
                >
                  {safeRender(header || title)}
                </div>

                {showCloseButton && (
                  <button
                    className={css`
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      width: 2rem;
                      height: 2rem;
                      background: none;
                      border: none;
                      border-radius: 6px;
                      color: ${Color.gray()};
                      cursor: pointer;
                      transition: all 0.2s ease;
                      flex-shrink: 0;

                      @media (hover: hover) and (pointer: fine) {
                        &:hover {
                          background-color: ${Color.borderGray()};
                          color: ${Color.black()};
                        }
                      }

                      &:focus-visible {
                        outline: 2px solid ${Color.logoBlue()};
                        outline-offset: 2px;
                      }
                    `}
                    onClick={onClose}
                    aria-label="Close modal"
                    type="button"
                  >
                    <Icon icon="times" />
                  </button>
                )}
              </div>
            )}

            {!hasHeader && showCloseButton && (
              <button
                className={css`
                  position: absolute;
                  top: ${deviceIsMobile ? '0.75rem' : '1rem'};
                  right: ${deviceIsMobile ? '0.75rem' : '1rem'};
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 2rem;
                  height: 2rem;
                  background: none;
                  border: none;
                  border-radius: 6px;
                  color: ${Color.gray()};
                  cursor: pointer;
                  transition: all 0.2s ease;
                  z-index: 1;

                  @media (hover: hover) and (pointer: fine) {
                    &:hover {
                      background-color: ${Color.borderGray()};
                      color: ${Color.black()};
                    }
                  }

                  &:focus-visible {
                    outline: 2px solid ${Color.logoBlue()};
                    outline-offset: 2px;
                  }
                `}
                onClick={onClose}
                aria-label="Close modal"
                type="button"
              >
                <Icon icon="times" />
              </button>
            )}

            <div
              className={css`
                flex: 1;
                min-height: 0;
                overflow-y: ${allowOverflow ? 'visible' : 'auto'};
                width: 100%;
                display: flex;
                justify-content: center;
                padding: ${resolvedBodyPadding};
                ${bodyBottomSpacingIsBorder
                  ? `padding-bottom: 0; border-bottom: ${resolvedBodyPadding} solid transparent;`
                  : ''}
                position: relative;
                font-size: 1.5rem;
              `}
            >
              {loading ? (
                <div
                  className={css`
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-height: 200px;
                    color: ${Color.gray()};
                  `}
                >
                  <Icon icon="spinner" pulse />
                  <span style={{ marginLeft: '0.5rem' }}>Loading...</span>
                </div>
              ) : (
                <ErrorBoundary
                  componentPath={
                    modalKey ? `${modalKey}/children` : 'Modal/children'
                  }
                >
                  <ModalFooterContext.Provider value={footerHost}>
                    <XpActivityContext.Provider value={marksXpActivity}>
                      {children}
                    </XpActivityContext.Provider>
                  </ModalFooterContext.Provider>
                </ErrorBoundary>
              )}
            </div>

            <div
              ref={setFooterContainer}
              className={css`
                width: 100%;
                flex-shrink: 0;
                &:empty { display: none; }
              `}
            >
              {footer && (
                <ModalFooterContext.Provider value={null}>
                  <ModalFooter fullscreen={size === 'fullscreen'}>
                    {safeRender(footer)}
                  </ModalFooter>
                </ModalFooterContext.Provider>
              )}
            </div>
          </div>
        </div>
      </ErrorBoundary>
    );

    if (portalRoot) return createPortal(modalContent, container);
    // Wait for the layout effect to attach an available portal container before
    // mounting children. An inline-to-portal move remounts them and repeats
    // effects (such as chat instruction generation); a detached portal would
    // instead give their first layout effects incorrect measurements.
    return portalTarget || document.getElementById('modal') ? null : modalContent;
  }
);

Modal.displayName = 'Modal';

// Set inside an XP activity's modal (xpActivity), for the windows it opens.
const XpActivityContext = createContext(false);

export default Modal;
